import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import backend from '@api/backend'
import {
  notifyApiError,
  notifyError,
  notifyInfo,
  notifySuccess,
} from '@common/utils/notify'
import { mapUser } from '@common/utils/mapUser'
import { describeRegistrationError } from './registrationError'
import { useAuthStore } from '@store/auth.store'

import User, { RawUserResponse } from '@data/users/model/user'

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

export function useCurrentUserQuery() {
  const authenticated = useAuthStore((state) => state.authenticated)
  return useQuery({
    queryKey: ['current-user'],
    queryFn: getCurrentUser,
    enabled: authenticated,
    staleTime: 1000 * 60 * 15,
    select: mapUser,
  })
}

async function getCurrentUser(): Promise<RawUserResponse> {
  const response = await backend.get<RawUserResponse>('/users/me/')
  return response.data
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useUpdateProfileMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
                         firstName,
                         lastName,
                         mobile,
                       }: {
      firstName: string
      lastName: string
      mobile: string
    }) => {
      const response = await backend.patch('/users/me/update_profile', {
        firstName,
        lastName,
        mobile,
      })
      return response.data
    },
    onSuccess: (data, variables) => {
      // This cache holds the *raw* payload that mapUser reads through `select`, so
      // these keys have to match the response shape, not the mapped User shape.
      queryClient.setQueryData<Record<string, any> | undefined>(['current-user'], (prev) =>
        prev
          ? {
            ...prev,
            firstName: data.firstName ?? variables.firstName,
            lastName: data.lastName ?? variables.lastName,
            mobile: data.mobile ?? variables.mobile,
          }
          : prev,
      )
      void queryClient.invalidateQueries({ queryKey: ['current-user'] })
      notifySuccess('User updated successfully')
    },
    onError: (error: any) => {
      console.log(error)
      notifyError(error?.response?.data?.detail || 'Something went wrong', {
        autoClose: 5000,
      })
    },
  })
}

export function useUpdateUserPreferencesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    // `firstName` became required on UserUpdate, so a preferences-only body is now
    // rejected with 422. The backend applies `exclude_unset`, so resending the
    // unchanged first name is a no-op and lastName/mobile stay untouched.
    mutationFn: async ({ firstName, uom }: { firstName: string; uom: string }) => {
      const response = await backend.patch('/users/me/update_profile', {
        firstName,
        userPreferences: { uom },
      })
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData<User>(['current-user'], (prev) =>
        prev ? { ...prev, preferences: data.preferences } : prev,
      )
      notifySuccess('User preferences updated successfully')
    },
    onError: (error: any) => {
      console.log(error)
      notifyError(error?.response?.data?.detail || 'Something went wrong', {
        autoClose: 5000,
      })
    },
  })
}

export function useChangePasswordMutation() {
  return useMutation({
    mutationFn: async ({
                         currentPassword,
                         newPassword,
                         verifyPassword,
                       }: {
      currentPassword: string
      newPassword: string
      verifyPassword: string
    }) => {
      const response = await backend.put('/users/me/change_password', {
        password: currentPassword,
        newPassword,
        verifyPassword,
      })
      return response.data
    },
    onSuccess: () => {
      notifySuccess('Password updated successfully')
      notifyInfo('You are being logging out...')
    },
    onError: (error: any) => {
      console.log(error)
      const errorCode = error?.response?.data?.error_code
      notifyError(errorCode === 20000 ? 'Current Password does not match' : error?.response?.data?.message || 'Something went wrong', {
        autoClose: 5000,
      })
    },
  })
}

export function useForgotPasswordMutation() {
  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      const response = await backend.post('/users/me/forgot_password', { email })
      return response.data
    },
    onSuccess: () => {
      notifySuccess('Email sent successfully')
    },
    onError: (error: any) => {
      console.log(error)
      notifyError(
        'An Error occurred while requesting your password reset email. Please try again...',
        {
          autoClose: 5000,
        },
      )
    },
  })
}

export function useResetPasswordMutation() {
  return useMutation({
    mutationFn: async ({
                         newPassword,
                         verifyPassword,
                         token,
                       }: {
      newPassword: string
      verifyPassword: string
      token: string
    }) => {
      const response = await backend.post(`/users/me/reset_password/${token}`, {
        newPassword,
        verifyPassword,
      })
      return response.data
    },
    onError: (error: any) => {
      console.log(error)
      if (error.response?.data?.error_code === 80004) {
        notifyError('Reset password link had expired, please request a new link from the forgot password page')
      } else {
        notifyApiError(error)
      }
    },
  })
}

export function useRegisterUserMutation() {
  return useMutation({
    mutationFn: async ({
                         firstname,
                         lastname,
                         mobile,
                         newPassword,
                         newPasswordConfirmation,
                         token,
                         uom,
                       }: {
      firstname: string
      lastname: string
      mobile: string
      newPassword: string
      newPasswordConfirmation: string
      token: string
      uom: string
    }) => {
      const response = await backend.post(`/users/register/${token}`, {
        firstName: firstname,
        lastName: lastname,
        mobile,
        password: newPassword,
        verifyPassword: newPasswordConfirmation,
        userPreferences: { uom },
      })
      return response.data
    },
    onSuccess: () => {
      notifySuccess('Account created successfully')
    },
    onError: (e: any) => {
      console.log(e)
      notifyError(describeRegistrationError(e?.response?.data), {
        autoClose: 5000,
      })
    },
  })
}

export function useConfirmEmailMutation() {
  return useMutation({
    mutationFn: async ({ token }: { token: string }) => {
      const response = await backend.post(`/users/confirm_email/${token}`)
      return response.data
    },
    onError: (error: any) => {
      console.log(error)
    },
  })
}

export function useRequestConfirmEmailMutation() {
  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      const response = await backend.post('/users/request_confirm_email', { email })
      return response.data
    },
    onError: (error: any) => {
      console.log(error)
    },
  })
}
